import React, { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  type BillingCadence,
  commercialPlans,
  getPlanPriceUsd,
  getPlanStartingPriceUsd,
  isPaidPlanCode,
  paidCommercialPlans,
  realEstatePricingSchema,
} from '../config/commercialPlans';
import { commercialProducts } from '../config/commercialProducts';
import { aiCapabilities } from '../config/aiCapabilities';
import {
  defaultSeatBand,
  isEnterpriseSeatBand,
  seatBands,
  type SeatBandCode,
} from '../config/seatBands';
import { DemoRequestForm } from '../components/forms/DemoRequestForm';
import { HomeButton, homeButtonClasses } from '../components/home/HomeButton';
import { HomeSectionHeader } from '../components/home/HomeSectionHeader';
import { HOME_SURFACES } from '../components/home/homeSurfaces';
import { PricingBandBillingSummary } from '../components/pricing/PricingBandBillingSummary';
import { PricingBillingToggle } from '../components/pricing/PricingBillingToggle';
import { PricingSeatBandToggle } from '../components/pricing/PricingSeatBandToggle';
import { trackMarketingEvent, useSectionViewTracking } from '../lib/analytics';
import { resolveCrmRegisterUrl } from '../lib/appUrls';
import { usePageMetadata } from '../lib/seo';
import { cn } from '../lib/utils';

import { MarketingPageCta } from '../components/marketing/MarketingPageCta';
import { MarketingPageHero } from '../components/marketing/MarketingPageHero';
import {
  marketingCardClass,
  marketingStatCardClass,
  marketingTableShellClass,
} from '../components/marketing/marketingSurfaces';
import { ComplianceBadges } from '../components/marketing/ComplianceBadges';
import { EnterprisePriceLabel } from '../components/EnterprisePriceLabel';
import { PricingMarketingHints } from '../components/PricingMarketingHints';
import { UsdPrice } from '../components/UsdPrice';
import { formatUsd } from '../lib/formatUsd';

const MARKETPLACE_URL = 'https://inmueble.red';

export function PricingPage(): React.JSX.Element {
  const { t } = useTranslation(['pricing', 'common', 'home', 'proof']);
  const [selectedBand, setSelectedBand] =
    useState<SeatBandCode>(defaultSeatBand);
  const [billingCadence, setBillingCadence] =
    useState<BillingCadence>('monthly');
  const crmRegisterUrl = resolveCrmRegisterUrl(
    import.meta.env.VITE_CRM_REGISTER_URL,
    import.meta.env.VITE_CRM_LOGIN_URL
  );

  usePageMetadata({
    title: t('pricing:meta.title'),
    description: t('pricing:meta.description'),
    path: '/precios',
  });

  const trustPanelRef = useSectionViewTracking('pricing_trust_panel');
  const lowestPaidPrice = Math.min(
    ...paidCommercialPlans.map((plan) =>
      getPlanStartingPriceUsd(plan.code, billingCadence)
    )
  );
  const selectedBandLabel = t(
    seatBands.find((band) => band.code === selectedBand)?.labelKey ??
      seatBands[0].labelKey
  );
  const seatBandLabels = useMemo(
    () =>
      Object.fromEntries(
        seatBands.map((band) => [band.code, t(band.labelKey)])
      ) as Record<SeatBandCode, string>,
    [t]
  );
  const priceUnitLabel =
    billingCadence === 'annual'
      ? t('pricing:priceUnitAnnual')
      : t('pricing:priceUnitMonthly');
  const billingNote =
    billingCadence === 'annual'
      ? t('pricing:billing.annualNote', {
          count: realEstatePricingSchema.annualBillingMonths,
          operate: realEstatePricingSchema.monthlyBillingMonths,
        })
      : t('pricing:billing.monthlyNote', {
          freeMonths: realEstatePricingSchema.annualFreeMonths,
        });
  const annualBadgeLabel = t('pricing:billing.annualBadge', {
    count: realEstatePricingSchema.annualFreeMonths,
  });
  const billingCadenceLabel =
    billingCadence === 'annual'
      ? t('pricing:billing.annual')
      : t('pricing:billing.monthly');

  const isEnterpriseBand = isEnterpriseSeatBand(selectedBand);

  const productNameByCode = useMemo(() => {
    return new Map(
      commercialProducts.map((product) => [product.code, t(product.nameKey)])
    );
  }, [t]);

  const aiNameByCode = useMemo(() => {
    return new Map(
      aiCapabilities.map((capability) => [
        capability.code,
        t(capability.titleKey),
      ])
    );
  }, [t]);
  const aiStatusByCode = useMemo(
    () =>
      new Map(
        aiCapabilities.map((capability) => [capability.code, capability.status])
      ),
    []
  );

  return (
    <div className="flex flex-col bg-brand-bg">
      <MarketingPageHero
        badge={t('pricing:hero.badge')}
        title={t('pricing:title')}
        subtitle={t('pricing:subtitle')}
        actions={
          <>
            <a
              href={crmRegisterUrl}
              className={homeButtonClasses('accent', 'lg')}
              onClick={() =>
                trackMarketingEvent('pricing_plan_select', {
                  planCode: 'free',
                  seatBand: selectedBand,
                  location: 'pricing_hero',
                })
              }
            >
              {t('common:cta.publishFree')}
            </a>
            <a
              href={MARKETPLACE_URL}
              className={homeButtonClasses('outline', 'lg')}
              onClick={() =>
                trackMarketingEvent('hero_cta_click', {
                  location: 'pricing_hero',
                  cta: 'browse_marketplace',
                })
              }
            >
              {t('common:cta.browseListings')}
            </a>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className={marketingStatCardClass}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
              {t('common:common.from')}
            </p>
            <p className="mt-3">
              <UsdPrice
                value={lowestPaidPrice}
                className="font-serif text-4xl tracking-tighter text-brand-primary"
              />
            </p>
            <p className="mt-2 text-xs font-medium text-[var(--ir-muted)]">
              {priceUnitLabel}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
              {t('pricing:fromPrice', {
                price: formatUsd(lowestPaidPrice),
              })}
            </p>
          </div>
          <div className={marketingStatCardClass}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
              {t('pricing:hero.currentBand')}
            </p>
            <p className="mt-3 font-serif text-2xl font-bold text-brand-primary">
              {selectedBandLabel}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
              {t('home:homePage.pricing.activeBandBody')}
            </p>
          </div>
          <div className={marketingStatCardClass}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
                {t('pricing:hero.currentBilling')}
              </p>
              {billingCadence === 'annual' ? (
                <span className="rounded-full bg-brand-accent px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.1em] text-white">
                  {annualBadgeLabel}
                </span>
              ) : null}
            </div>
            <p className="mt-3 font-serif text-2xl font-bold text-brand-primary">
              {billingCadenceLabel}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
              {billingNote}
            </p>
          </div>
        </div>
      </MarketingPageHero>

      <section className={HOME_SURFACES.ivory}>
        <div className="section-padding !pt-0">
          <HomeSectionHeader
            badge={t('pricing:calculator.badge')}
            title={t('pricing:calculator.title')}
            subtitle={t('pricing:calculator.helper')}
            className="mb-12 md:mb-16"
          />

          <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-[var(--ir-muted)]">
                {t('pricing:matrix.licenses')}
              </p>
              <PricingSeatBandToggle
                selectedBand={selectedBand}
                onSelect={setSelectedBand}
                labelForBand={(band) => seatBandLabels[band]}
              />
              <PricingMarketingHints
                seatBand={selectedBand}
                billingCadence={billingCadence}
                tone="home"
                className="mt-4"
              />
            </div>

            <PricingBillingToggle
              billingCadence={billingCadence}
              onSelect={setBillingCadence}
              title={t('pricing:billing.title')}
              monthlyLabel={t('pricing:billing.monthly')}
              annualLabel={t('pricing:billing.annual')}
              annualBadge={annualBadgeLabel}
            />
          </div>

          <PricingBandBillingSummary
            className="mb-10"
            bandKicker={t('pricing:hero.currentBand')}
            bandLabel={selectedBandLabel}
            bandBody={t('home:homePage.pricing.activeBandBody')}
            billingKicker={t('pricing:hero.currentBilling')}
            billingLabel={billingCadenceLabel}
            billingNote={billingNote}
            billingCadence={billingCadence}
            annualBadge={annualBadgeLabel}
          />

          <div
            id="pricing-grid"
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4"
          >
            {commercialPlans.map((plan) => {
              const isPopular = plan.popular;
              const isSelfServicePlan = plan.ctaType === 'self_service';
              const paidPlanCode = isPaidPlanCode(plan.code) ? plan.code : null;
              const isPaidPlan = paidPlanCode !== null;
              const selectedPrice = paidPlanCode
                ? getPlanPriceUsd(paidPlanCode, selectedBand, billingCadence)
                : null;
              const startingPrice = paidPlanCode
                ? getPlanStartingPriceUsd(paidPlanCode, billingCadence)
                : null;
              const usesEnterprisePrice = isEnterpriseBand && isPaidPlan;
              if (
                isPaidPlan &&
                !usesEnterprisePrice &&
                selectedPrice === null
              ) {
                throw new Error(
                  `Missing pricing for plan ${plan.code} in ${selectedBand} band`
                );
              }

              return (
                <article
                  key={plan.code}
                  data-testid={`pricing-plan-${plan.code}`}
                  className={cn(
                    'relative flex flex-col rounded-[1.5rem] border border-brand-primary/10 p-8',
                    isPopular
                      ? 'bg-brand-primary text-white shadow-[0_26px_80px_-50px_rgba(0,31,34,0.78)]'
                      : 'bg-white text-brand-primary shadow-[0_20px_60px_-55px_rgba(0,31,34,0.65)]'
                  )}
                >
                  <div className="mb-6 grid min-h-6 grid-cols-2 gap-2">
                    <div
                      className="flex justify-start"
                      data-testid="recommendation-badge-slot"
                    >
                      {isPopular ? (
                        <div className="rounded-full bg-brand-accent px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-white">
                          {t('pricing:plans.popular')}
                        </div>
                      ) : null}
                    </div>
                    <div
                      className="flex justify-end"
                      data-testid="self-service-badge-slot"
                    >
                      {isSelfServicePlan ? (
                        <div className="rounded-full bg-brand-teal px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-white">
                          {t('common:cta.freePlanSignup')}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <h2 className="mb-4 font-serif text-3xl font-bold">
                    {t(plan.nameKey)}
                  </h2>
                  <p
                    className={cn(
                      'mb-8 leading-relaxed',
                      isPopular ? 'text-white/60' : 'text-[var(--ir-muted)]'
                    )}
                  >
                    {t(plan.descriptionKey)}
                  </p>

                  <div className="mb-8">
                    <span
                      className={cn(
                        'mb-2 block text-xs uppercase tracking-[0.16em]',
                        isPopular ? 'text-white/70' : 'text-[var(--ir-muted)]'
                      )}
                    >
                      {isSelfServicePlan
                        ? t('pricing:freePriceCaption')
                        : t('pricing:priceCaption')}
                    </span>
                    {isSelfServicePlan ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <UsdPrice
                            value={
                              realEstatePricingSchema.freeTerms.monthlyPriceUsd
                            }
                            className="font-serif text-5xl tracking-tighter"
                          />
                          <span className="font-medium text-[var(--ir-muted)]">
                            {t('pricing:freePriceUnit')}
                          </span>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-[var(--ir-muted)]">
                          {t('pricing:freePriceNote', {
                            individualUsers:
                              realEstatePricingSchema.freeTerms
                                .individualActiveUsers,
                            businessUsers:
                              realEstatePricingSchema.freeTerms
                                .businessActiveUsers,
                          })}
                        </p>
                      </>
                    ) : usesEnterprisePrice ? (
                      <EnterprisePriceLabel
                        variant={isPopular ? 'onDark' : 'default'}
                      />
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <UsdPrice
                            value={selectedPrice as number}
                            variant={isPopular ? 'onDark' : 'default'}
                            className="font-serif text-5xl tracking-tighter"
                          />
                          <span
                            className={cn(
                              'font-medium',
                              isPopular
                                ? 'text-white/70'
                                : 'text-[var(--ir-muted)]'
                            )}
                          >
                            {priceUnitLabel}
                          </span>
                        </div>
                        <p
                          className={cn(
                            'mt-3 text-xs tracking-[0.12em]',
                            isPopular
                              ? 'text-white/70'
                              : 'text-[var(--ir-muted)]'
                          )}
                        >
                          {t('pricing:fromPrice', {
                            price: formatUsd(startingPrice as number),
                          })}
                        </p>
                        <p
                          className={cn(
                            'mt-4 text-sm leading-relaxed',
                            isPopular
                              ? 'text-white/60'
                              : 'text-[var(--ir-muted)]'
                          )}
                        >
                          {billingNote}
                        </p>
                      </>
                    )}
                  </div>

                  <div
                    className={cn(
                      'mb-8 flex-grow border-t pt-8',
                      isPopular ? 'border-white/10' : 'border-brand-primary/5'
                    )}
                  >
                    <p
                      className={cn(
                        'mb-4 text-xs font-bold uppercase tracking-[0.14em]',
                        isPopular ? 'text-white/70' : 'text-[var(--ir-muted)]'
                      )}
                    >
                      {t('pricing:includes')}
                    </p>
                    <ul className="space-y-4">
                      {plan.includesModules.map((moduleCode) => (
                        <li
                          key={moduleCode}
                          className="flex items-start gap-3 font-medium"
                        >
                          <Check
                            size={20}
                            className={
                              isPopular
                                ? 'text-brand-accent'
                                : 'text-brand-teal'
                            }
                          />
                          <span
                            className={isPopular ? 'text-white/90' : undefined}
                          >
                            {productNameByCode.get(moduleCode) ?? moduleCode}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className={cn(
                      'mb-8 border-t pt-8',
                      isPopular ? 'border-white/10' : 'border-brand-primary/5'
                    )}
                  >
                    <p
                      className={cn(
                        'mb-4 text-xs font-bold uppercase tracking-[0.14em]',
                        isPopular ? 'text-white/70' : 'text-[var(--ir-muted)]'
                      )}
                    >
                      {t('pricing:aiIncluded')}
                    </p>
                    <ul className="space-y-3">
                      {plan.featuredAiCapabilities.map((capabilityCode) => (
                        <li
                          key={capabilityCode}
                          className={cn(
                            'text-xs font-semibold',
                            isPopular ? 'text-white/85' : 'text-brand-teal'
                          )}
                        >
                          <span>
                            {aiNameByCode.get(capabilityCode) ?? capabilityCode}
                          </span>
                          {aiStatusByCode.get(capabilityCode) ===
                          'coming_soon' ? (
                            <span
                              className={cn(
                                'ml-2 text-xs uppercase tracking-[0.12em]',
                                isPopular
                                  ? 'text-[var(--ir-clay)]'
                                  : 'text-brand-accent-ink'
                              )}
                            >
                              {t('common:common.comingSoon')}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {isSelfServicePlan ? (
                    <a
                      href={crmRegisterUrl}
                      className={cn(
                        homeButtonClasses('accent', 'md'),
                        'w-full justify-center'
                      )}
                      onClick={() => {
                        trackMarketingEvent('pricing_plan_select', {
                          planCode: plan.code,
                          seatBand: selectedBand,
                          location: 'pricing_plan_card',
                        });
                      }}
                    >
                      {t('common:cta.startFree')}
                    </a>
                  ) : (
                    <HomeButton
                      variant={isPopular ? 'accent' : 'secondary'}
                      className="w-full"
                      onClick={() => {
                        trackMarketingEvent('pricing_plan_select', {
                          planCode: plan.code,
                          seatBand: selectedBand,
                          location: 'pricing_plan_card',
                        });
                        document
                          .getElementById('demo-form')
                          ?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      {t('common:cta.requestDemo')}
                    </HomeButton>
                  )}
                </article>
              );
            })}
          </div>

          <p className="mx-auto mt-10 max-w-3xl rounded-full border border-brand-primary/10 bg-brand-bg px-5 py-3 text-center text-xs text-[var(--ir-muted)]">
            {t('home:homePage.pricing.disclaimer')}
          </p>
        </div>
      </section>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('pricing:matrix.badge')}
            title={t('pricing:matrix.title')}
            subtitle={t('pricing:matrix.note')}
            className="mb-12 md:mb-16"
          />

          <div
            className="mb-10 grid gap-5 border-y border-brand-primary/10 py-8 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] md:items-center"
            data-testid="free-plan-fixed-terms"
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">
                {t('pricing:freeTerms.badge')}
              </p>
              <h3 className="mt-3 font-serif text-3xl font-bold text-brand-primary">
                {t('pricing:plans.free.name')}
              </h3>
            </div>
            <p className="text-base leading-relaxed text-[var(--ir-muted)]">
              {t('pricing:freeTerms.body', {
                individualUsers:
                  realEstatePricingSchema.freeTerms.individualActiveUsers,
                businessUsers:
                  realEstatePricingSchema.freeTerms.businessActiveUsers,
              })}
            </p>
          </div>

          <div className={marketingTableShellClass}>
            <table className="min-w-full text-left">
              <thead>
                <tr className="border-b border-brand-primary/10 bg-brand-bg/60">
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
                    {t('pricing:matrix.licenses')}
                  </th>
                  {paidCommercialPlans.map((plan) => (
                    <th
                      key={plan.code}
                      className="px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]"
                    >
                      {t(plan.nameKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {seatBands.map((band) => (
                  <tr
                    key={band.code}
                    className="border-b border-brand-primary/5 last:border-b-0"
                  >
                    <td className="px-6 py-4 font-medium text-brand-primary">
                      {t(band.labelKey)}
                    </td>
                    {paidCommercialPlans.map((plan) => {
                      const usesEnterpriseCell = band.isEnterprise;
                      const matrixPrice = getPlanPriceUsd(
                        plan.code,
                        band.code,
                        billingCadence
                      );

                      if (usesEnterpriseCell) {
                        return (
                          <td
                            key={`${plan.code}-${band.code}`}
                            className="px-6 py-4 text-brand-primary"
                          >
                            <span className="text-sm text-[var(--ir-muted)]">
                              {t('pricing:enterprise.matrixCell')}
                            </span>
                          </td>
                        );
                      }

                      if (matrixPrice === null) {
                        throw new Error(
                          `Missing matrix pricing for plan ${plan.code} in ${band.code} band`
                        );
                      }

                      return (
                        <td
                          key={`${plan.code}-${band.code}`}
                          className="px-6 py-4 text-brand-primary"
                        >
                          <UsdPrice value={matrixPrice} className="text-base" />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section ref={trustPanelRef}>
        <MarketingPageCta
          badge={t('pricing:trust.badge')}
          title={t('proof:pricing.trustPanelTitle')}
          subtitle={t('proof:pricing.trustPanelBody')}
          actions={
            <HomeButton
              variant="accent"
              size="lg"
              onClick={() => {
                trackMarketingEvent('trust_band_cta_click', {
                  location: 'pricing_trust_panel',
                  cta: 'request_demo',
                });
                document
                  .getElementById('demo-form')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('proof:pricing.trustPanelCta')}
            </HomeButton>
          }
        />
      </section>

      <section id="demo-form" className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2 lg:gap-24">
            <div>
              <HomeSectionHeader
                badge={t('pricing:contact.badge')}
                title={t('home:cta.title')}
                subtitle={t('pricing:demoSubtitle')}
                className="mb-10"
              />
              <ComplianceBadges />
            </div>

            <div className={cn(marketingCardClass, 'p-8 md:p-12')}>
              <DemoRequestForm
                variant="full"
                appearance="underlined"
                submitLabel={t('common:cta.requestDemo')}
                helperText={t('pricing:demoHelperText')}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
